import { Link, router } from '@inertiajs/react'
import { useEffect, useRef, useState } from 'react'
import { useOpsMonitor } from './Index'
import InfoTip from '../../../components/InfoTip'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatAt(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function timeAgo(iso) {
  if (!iso) return '—'
  const sec = Math.round((Date.now() - new Date(iso).getTime()) / 1000)
  if (sec < 5)  return 'just now'
  if (sec < 60) return `${sec}s ago`
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`
  return `${Math.round(sec / 3600)}h ago`
}

// ─── Constants ────────────────────────────────────────────────────────────────

const SKILL_NAMES = [
  'pnr_ingest', 'ava_insights', 'irop_event_push', 'msp_dispatch',
  'vuuoffer_push', 'attestation_push', 'dispatch_status',
]

const SKILL_META = {
  pnr_ingest:       { color: '#2196F3', icon: '📥', label: 'PNR Ingest' },
  ava_insights:     { color: '#9C27B0', icon: '🧠', label: 'AVA Insights' },
  irop_event_push:  { color: '#F44336', icon: '⚠', label: 'IROP Push' },
  msp_dispatch:     { color: '#FF9800', icon: '🚀', label: 'MSP Dispatch' },
  vuuoffer_push:    { color: '#00BCD4', icon: '🎫', label: 'vuuOffer Push' },
  attestation_push: { color: '#4CAF50', icon: '✅', label: 'Attestation Push' },
  dispatch_status:  { color: '#607D8B', icon: '📡', label: 'Dispatch Status' },
}

const STATUS_COLORS = {
  success:  '#4CAF50',
  degraded: '#FF9800',
  error:    '#F44336',
}

const LATENCY_COLOR = ms => {
  if (!ms) return '#9E9E9E'
  if (ms < 200)  return '#4CAF50'
  if (ms < 800)  return '#FF9800'
  return '#F44336'
}

// ─── SkillRow ─────────────────────────────────────────────────────────────────

function SkillRow({ log, live = false }) {
  const meta        = SKILL_META[log.skill] || { color: '#9E9E9E', icon: '?', label: log.skill }
  const statusColor = STATUS_COLORS[log.status] || '#9E9E9E'
  const latColor    = LATENCY_COLOR(log.latency_ms)

  return (
    <tr className={`border-b border-gray-100 hover:bg-gray-50 ${live ? 'bg-blue-50' : ''}`}>
      {/* Skill badge */}
      <td className="py-2 px-3 whitespace-nowrap">
        <span
          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-bold border"
          style={{ backgroundColor: meta.color + '22', color: meta.color, borderColor: meta.color }}
        >
          <span>{meta.icon}</span>
          {meta.label}
          {live && <span className="ml-1 w-1.5 h-1.5 rounded-full bg-blue-500 inline-block animate-pulse" />}
        </span>
      </td>

      {/* PNR locator */}
      <td className="py-2 px-3 whitespace-nowrap">
        {log.pnr ? (
          <a
            href={`/admin/cloudtraq_gds/pnr/${log.pnr}`}
            className="font-mono text-xs font-semibold text-indigo-600 hover:underline"
          >
            {log.pnr}
          </a>
        ) : (
          <span className="text-gray-400 text-xs">—</span>
        )}
      </td>

      {/* Carrier */}
      <td className="py-2 px-3 whitespace-nowrap">
        <span className="font-mono text-xs font-bold text-gray-700">{log.carrier || '—'}</span>
      </td>

      {/* GDS source */}
      <td className="py-2 px-3 whitespace-nowrap">
        <span className="text-xs text-gray-500">{log.gds_source || '—'}</span>
      </td>

      {/* Risk score */}
      <td className="py-2 px-3 whitespace-nowrap text-right">
        {log.risk != null ? (
          <span
            className="text-xs font-bold tabular-nums"
            style={{ color: log.risk >= 0.7 ? '#F44336' : log.risk >= 0.4 ? '#FF9800' : '#4CAF50' }}
          >
            {log.risk.toFixed(2)}
          </span>
        ) : (
          <span className="text-gray-400 text-xs">—</span>
        )}
      </td>

      {/* Latency */}
      <td className="py-2 px-3 whitespace-nowrap text-right">
        {log.latency_ms != null ? (
          <span className="text-xs font-mono font-bold tabular-nums" style={{ color: latColor }}>
            {log.latency_ms}ms
          </span>
        ) : (
          <span className="text-gray-400 text-xs">—</span>
        )}
      </td>

      {/* Status */}
      <td className="py-2 px-3 whitespace-nowrap text-center">
        <span
          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border"
          style={{ backgroundColor: statusColor + '22', color: statusColor, borderColor: statusColor }}
        >
          {log.status || '—'}
        </span>
      </td>

      {/* Timestamp */}
      <td className="py-2 px-3 whitespace-nowrap">
        <span className="text-xs text-gray-500" title={formatAt(log.occurred_at)}>
          {timeAgo(log.occurred_at)}
        </span>
      </td>
    </tr>
  )
}

// ─── Latency sparkline ────────────────────────────────────────────────────────
// SVG sparkline of last-200 latencies by skill (shows SLA trend)

function LatencySparkline({ logs }) {
  const bySkill = {}
  logs.forEach(l => {
    if (l.latency_ms == null) return
    if (!bySkill[l.skill]) bySkill[l.skill] = []
    bySkill[l.skill].push(l.latency_ms)
  })

  const skills = Object.entries(bySkill).slice(0, 4)
  if (skills.length === 0) return null

  const W = 120, H = 32, PAD = 2

  function sparkPath(vals) {
    if (vals.length < 2) return null
    const mn = Math.min(...vals), mx = Math.max(...vals)
    const range = mx - mn || 1
    const pts   = vals.slice(-20).map((v, i, arr) => {
      const x = PAD + (i / (arr.length - 1)) * (W - PAD * 2)
      const y = H - PAD - ((v - mn) / range) * (H - PAD * 2)
      return `${x.toFixed(1)},${y.toFixed(1)}`
    })
    return `M ${pts.join(' L ')}`
  }

  return (
    <div className="flex gap-4 flex-wrap">
      {skills.map(([skill, vals]) => {
        const meta = SKILL_META[skill] || { color: '#9E9E9E', label: skill }
        const path = sparkPath(vals)
        const avg  = Math.round(vals.reduce((a, b) => a + b, 0) / vals.length)
        return (
          <div key={skill} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded px-3 py-2">
            <div>
              <p className="text-xs font-semibold" style={{ color: meta.color }}>{meta.label}</p>
              <p className="text-xs text-gray-400">avg {avg}ms · {vals.length} calls</p>
            </div>
            {path && (
              <svg width={W} height={H} className="flex-shrink-0">
                <path d={path} fill="none" stroke={meta.color} strokeWidth={1.5} strokeLinecap="round" />
              </svg>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function SkillFeed({ skill_logs, page, per, filters, tenant_branding }) {
  const [rows,       setRows]       = useState(skill_logs)
  const [filterSkill, setFilterSkill] = useState(filters.skill_name || '')
  const [filterStat,  setFilterStat]  = useState(filters.response_status || '')
  const [liveCount,   setLiveCount]   = useState(0)

  // Sync rows with incoming props (after router.reload)
  useEffect(() => { setRows(skill_logs) }, [skill_logs])

  // Real-time injection via OpsMonitorChannel
  useOpsMonitor({
    tenantKey: tenant_branding?.tenant_key || 'cloudtraq',
    onEvent: (event, payload) => {
      if (event !== 'SKILL_INVOKED' || !payload) return
      // Filter to match current filter state
      if (filterSkill && payload.skill !== filterSkill) return
      if (filterStat  && payload.status !== filterStat) return
      setRows(prev => [{ ...payload, _live: true }, ...prev].slice(0, 200))
      setLiveCount(n => n + 1)
    },
  })

  function applyFilter() {
    const params = {}
    if (filterSkill) params.skill_name       = filterSkill
    if (filterStat)  params.response_status  = filterStat
    router.get('/admin/cloudtraq_gds/skill_feed', params, { preserveScroll: true })
  }

  function clearFilter() {
    setFilterSkill('')
    setFilterStat('')
    router.get('/admin/cloudtraq_gds/skill_feed', {}, { preserveScroll: true })
  }

  // Pagination
  const prevPage = page > 1 ? page - 1 : null
  const nextPage = rows.length === per ? page + 1 : null

  function goPage(p) {
    const params = { page: p }
    if (filterSkill) params.skill_name      = filterSkill
    if (filterStat)  params.response_status = filterStat
    router.get('/admin/cloudtraq_gds/skill_feed', params)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">ADK Skill Activity Feed</h1>
            <p className="text-sm text-gray-500 mt-1">
              IBM watsonx Orchestrate → cloudTraQ ADK skill invocations ·
              {rows.length} shown
              {liveCount > 0 && (
                <span className="ml-1.5 text-blue-600 font-semibold">· +{liveCount} live</span>
              )}
            </p>
          </div>
          <Link href="/admin/cloudtraq_gds" className="text-sm text-blue-600 hover:underline">← Command Center</Link>
        </div>

        {/* Latency sparklines */}
        {rows.length > 0 && (
          <div className="mb-4">
            <LatencySparkline logs={rows} />
          </div>
        )}

        {/* Filters */}
        <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 mb-4 flex items-center gap-3 flex-wrap">
          <select
            value={filterSkill}
            onChange={e => setFilterSkill(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1 text-sm text-gray-700"
          >
            <option value="">All skills</option>
            {SKILL_NAMES.map(s => (
              <option key={s} value={s}>{SKILL_META[s]?.label || s}</option>
            ))}
          </select>
          <select
            value={filterStat}
            onChange={e => setFilterStat(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1 text-sm text-gray-700"
          >
            <option value="">All statuses</option>
            <option value="success">Success</option>
            <option value="degraded">Degraded</option>
            <option value="error">Error</option>
          </select>
          <button onClick={applyFilter} className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">
            Filter
          </button>
          {(filterSkill || filterStat) && (
            <button onClick={clearFilter} className="text-sm text-gray-500 hover:text-gray-800 underline">
              Clear
            </button>
          )}
          <span className="ml-auto text-xs text-gray-400">
            {liveCount > 0 && <span className="text-blue-500 font-semibold mr-2">● LIVE</span>}
            Page {page} · {rows.length} rows
          </span>
        </div>

        {/* Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="py-2 px-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Skill</th>
                  <th className="py-2 px-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">PNR</th>
                  <th className="py-2 px-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Carrier</th>
                  <th className="py-2 px-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">GDS Source</th>
                  <th className="py-2 px-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Risk</th>
                  <th className="py-2 px-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Latency</th>
                  <th className="py-2 px-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="py-2 px-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Time</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-gray-400 text-sm">
                      No skill invocations recorded yet.
                    </td>
                  </tr>
                ) : (
                  rows.map((log, i) => (
                    <SkillRow key={log.id || `row-${i}`} log={log} live={log._live} />
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {(prevPage || nextPage) && (
            <div className="border-t border-gray-100 px-4 py-2 flex items-center justify-between text-sm">
              <button
                onClick={() => prevPage && goPage(prevPage)}
                disabled={!prevPage}
                className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40"
              >
                ← Prev
              </button>
              <span className="text-gray-500">Page {page}</span>
              <button
                onClick={() => nextPage && goPage(nextPage)}
                disabled={!nextPage}
                className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40"
              >
                Next →
              </button>
            </div>
          )}
        </div>

        {/* Nav */}
        <div className="mt-6 flex flex-wrap gap-4 text-sm text-gray-500">
          <Link href="/admin/cloudtraq_gds" className="text-blue-600 hover:underline">← Command Center</Link>
          <Link href="/admin/cloudtraq_gds/channel_sensor" className="text-blue-600 hover:underline">channelSensor Ops</Link>
          <Link href="/admin/cloudtraq_gds/high_risk" className="text-blue-600 hover:underline">High Risk Candidates</Link>
        </div>

      </div>
    </div>
  )
}
